import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { toast } from 'sonner';
import { Plus, X, Trash2, Pencil, Eye, Printer, Search, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useGlobalBarcodeScanner } from '@/hooks/useGlobalBarcodeScanner';
import { usePermissions } from '@/hooks/use-permissions';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FABRIC_TYPES = [
  'LINEN/COTTON/NATURAL',
  'SILK/SATIN',
  'KNIT',
  'GEORGETTE/CHIFFON/CREPE',
  'OTHER'
];

export default function FabricData({ user }) {
  const navigate = useNavigate();
  const { canCreate, canEdit, canDelete } = usePermissions(user);
  const [fabrics, setFabrics] = useState([]);
  const [filteredFabrics, setFilteredFabrics] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [gsmMin, setGsmMin] = useState('');
  const [gsmMax, setGsmMax] = useState('');
  const [fabricTypeFilter, setFabricTypeFilter] = useState('');
  const [customFabricTypeFilter, setCustomFabricTypeFilter] = useState('');
  const [colorNameFilter, setColorNameFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [vendorCodeFilter, setVendorCodeFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [editFabric, setEditFabric] = useState(null);
  const [viewDetails, setViewDetails] = useState(null);
  const [colorSearchQuery, setColorSearchQuery] = useState('');
  const [printBarcode, setPrintBarcode] = useState(null);
  const [printSize, setPrintSize] = useState({ width: 50, height: 30 });
  const [formData, setFormData] = useState({
    ln_code: 'LN-', vendor_name: '', vendor_code: '', composition: '', width: '', gsm: '', fabric_type: '', colors: []
  });
  const [customFabricType, setCustomFabricType] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fabricToDelete, setFabricToDelete] = useState(null);
  const [deleteColorDialogOpen, setDeleteColorDialogOpen] = useState(false);
  const [colorToDelete, setColorToDelete] = useState(null);
  const [currentColor, setCurrentColor] = useState({
    color_code: '', color_name: '', price: '', uom: 'meter', price_per_meter: ''
  });
  const [editingColorIndex, setEditingColorIndex] = useState(null);
  const [vendorDetailsModal, setVendorDetailsModal] = useState(null);

  // Global barcode scanner - works anywhere on the page
  const handleGlobalBarcodeScanned = (scannedCode) => {
    console.log('🔍 Fabric page received barcode:', scannedCode);
    
    // Search for fabric by main code or color code
    const found = fabrics.find(fabric => {
      // Check exact match for main LN code
      if (fabric.ln_code.toLowerCase() === scannedCode.toLowerCase()) {
        return true;
      }
      
      // Check if scanned code matches any color barcode (e.g., "LN-00045-003")
      if (fabric.colors?.some(color => {
        const colorBarcodeCode = `${fabric.ln_code}-${color.color_code}`.toLowerCase();
        return colorBarcodeCode === scannedCode.toLowerCase();
      })) {
        return true;
      }
      
      return false;
    });
    
    if (found) {
      setViewDetails(found);
      
      // If it's a color code search, highlight that color
      const isColorCode = scannedCode.includes('-') && scannedCode.split('-').length > 2;
      if (isColorCode) {
        const colorCode = scannedCode.split('-').pop();
        setColorSearchQuery(colorCode);
      } else {
        setColorSearchQuery('');
      }
      
      toast.success(`✅ Fabric found: ${found.ln_code}`);
    } else {
      toast.error(`❌ No fabric found with barcode: ${scannedCode}`);
    }
  };

  // Use the global barcode scanner hook
  useGlobalBarcodeScanner(handleGlobalBarcodeScanned);

  useEffect(() => {
    fetchFabrics();
    fetchVendors();
  }, []);

  useEffect(() => {
    let filtered = fabrics;

    // Filter by LN Code or Color Code (for barcode scanning)
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(fabric => {
        // Check main LN code
        if (fabric.ln_code.toLowerCase().includes(query)) {
          return true;
        }
        
        // Check if search query matches any color barcode (e.g., "LN-00045-003")
        if (fabric.colors?.some(color => {
          const colorBarcodeCode = `${fabric.ln_code}-${color.color_code}`.toLowerCase();
          return colorBarcodeCode === query || colorBarcodeCode.includes(query);
        })) {
          return true;
        }
        
        // Check if search query is exact match for main barcode
        if (fabric.ln_code.toLowerCase() === query) {
          return true;
        }
        
        return false;
      });
    }

    // Filter by GSM Range
    if (gsmMin !== '' || gsmMax !== '') {
      filtered = filtered.filter(fabric => {
        const min = gsmMin === '' ? 0 : parseFloat(gsmMin);
        const max = gsmMax === '' ? Infinity : parseFloat(gsmMax);
        return fabric.gsm >= min && fabric.gsm <= max;
      });
    }

    // Filter by Fabric Type
    if (fabricTypeFilter !== '') {
      const filterValue = fabricTypeFilter.startsWith('OTHER:') ? fabricTypeFilter : fabricTypeFilter;
      filtered = filtered.filter(fabric => {
        if (!fabric.fabric_type) return false;
        if (filterValue.startsWith('OTHER:')) {
          return fabric.fabric_type.toLowerCase().includes(filterValue.toLowerCase());
        }
        return fabric.fabric_type === filterValue;
      });
    }

    // Filter by Color Name
    if (colorNameFilter.trim() !== '') {
      const colorQuery = colorNameFilter.toLowerCase();
      filtered = filtered.filter(fabric => 
        fabric.colors?.some(color => 
          color.color_name.toLowerCase().includes(colorQuery)
        )
      );
    }

    // Filter by Supplier
    if (supplierFilter.trim() !== '') {
      filtered = filtered.filter(fabric => 
        fabric.vendor_name && fabric.vendor_name.toLowerCase().includes(supplierFilter.toLowerCase())
      );
    }

    // Filter by Vendor Code
    if (vendorCodeFilter.trim() !== '') {
      filtered = filtered.filter(fabric => 
        fabric.vendor_code && fabric.vendor_code.toLowerCase().includes(vendorCodeFilter.toLowerCase())
      );
    }

    setFilteredFabrics(filtered);
  }, [searchQuery, gsmMin, gsmMax, fabricTypeFilter, colorNameFilter, supplierFilter, vendorCodeFilter, fabrics]);

  const extractNumeric = (code) => {
    return code.replace(/\D/g, '').replace(/^0+/, '');
  };

  const handleBarcodeSearch = (e) => {
    if (e && e.key === 'Enter' && searchQuery.trim() !== '') {
      e.preventDefault();
      const query = searchQuery.toLowerCase().trim();
      
      console.log('🔍 Searching for:', query);
      
      // Find fabric by main code or color code
      const found = fabrics.find(fabric => {
        // Check exact match for main LN code
        if (fabric.ln_code.toLowerCase() === query) {
          return true;
        }
        
        // Check partial match for main LN code
        if (fabric.ln_code.toLowerCase().includes(query)) {
          return true;
        }
        
        // Check if query matches any color barcode (e.g., "LN-00045-003")
        if (fabric.colors?.some(color => {
          const colorBarcodeCode = `${fabric.ln_code}-${color.color_code}`.toLowerCase();
          return colorBarcodeCode === query || colorBarcodeCode.includes(query);
        })) {
          return true;
        }
        
        return false;
      });
      
      if (found) {
        console.log('✅ Found fabric:', found.ln_code);
        setViewDetails(found);
        
        // If it's a color code search, highlight that color
        const colorMatch = query.includes('-');
        if (colorMatch) {
          const colorCode = query.split('-').pop();
          setColorSearchQuery(colorCode);
        } else {
          setColorSearchQuery('');
        }
        
        toast.success(`✅ Found: ${found.ln_code}`);
      } else {
        console.log('❌ No fabric found for:', query);
        toast.error(`❌ No fabric found: ${query}`);
      }
    }
  };

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data);
    } catch (error) {
      console.error('Failed to fetch vendors');
    }
  };

  const handleVendorClick = (vendorName) => {
    const vendor = vendors.find(v => v.vendor_name === vendorName);
    if (vendor) {
      setVendorDetailsModal(vendor);
    }
  };

  const fetchFabrics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/fabrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFabrics(response.data);
      setFilteredFabrics(response.data);
    } catch (error) {
      toast.error('Failed to fetch fabrics');
    }
  };

  // Auto-refresh every 5 seconds, but pause when user is editing
  const isUserEditing = open || viewDetails !== null || editFabric !== null;
  useAutoRefresh(fetchFabrics, 5, isUserEditing);

  const addColor = () => {
    if (!currentColor.color_code || !currentColor.color_name) {
      toast.error('Please fill color code and color name');
      return;
    }
    
    // Clean color data before adding - convert empty/invalid values to null, with NaN protection
    const priceValue = (currentColor.price && currentColor.price !== '' && currentColor.price !== '0') ? parseFloat(currentColor.price) : null;
    const pricePerMeterValue = (currentColor.price_per_meter && currentColor.price_per_meter !== '' && currentColor.price_per_meter !== '0') ? parseFloat(currentColor.price_per_meter) : null;
    
    const cleanedColor = {
      color_code: currentColor.color_code,
      color_name: currentColor.color_name,
      price: (priceValue !== null && !isNaN(priceValue)) ? priceValue : null,
      uom: currentColor.uom || 'meter',
      price_per_meter: (pricePerMeterValue !== null && !isNaN(pricePerMeterValue)) ? pricePerMeterValue : null
    };
    
    if (editingColorIndex !== null) {
      const updatedColors = [...formData.colors];
      updatedColors[editingColorIndex] = cleanedColor;
      setFormData({ ...formData, colors: updatedColors });
      setEditingColorIndex(null);
    } else {
      setFormData({ ...formData, colors: [...formData.colors, cleanedColor] });
    }
    setCurrentColor({ color_code: '', color_name: '', price: '', uom: 'meter', price_per_meter: '' });
  };

  const editColor = (index) => {
    const color = formData.colors[index];
    setCurrentColor({ ...color });
    setEditingColorIndex(index);
  };

  const removeColor = (index) => {
    handleRemoveColor(index);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.colors.length === 0) {
      toast.error('Please add at least one color');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      
      // Clean up color data - convert empty/invalid values to null, with NaN protection
      const cleanedData = {
        ...formData,
        // Convert empty strings to null for numeric fields
        width: formData.width === '' || formData.width === null ? null : parseFloat(formData.width),
        gsm: formData.gsm === '' || formData.gsm === null ? null : parseFloat(formData.gsm),
        colors: formData.colors.map(color => {
          const priceValue = (color.price && color.price !== '' && color.price !== '0') ? parseFloat(color.price) : null;
          const pricePerMeterValue = (color.price_per_meter && color.price_per_meter !== '' && color.price_per_meter !== '0') ? parseFloat(color.price_per_meter) : null;
          
          return {
            ...color,
            price: (priceValue !== null && !isNaN(priceValue)) ? priceValue : null,
            price_per_meter: (pricePerMeterValue !== null && !isNaN(pricePerMeterValue)) ? pricePerMeterValue : null
          };
        })
      };
      
      if (editFabric) {
        await axios.put(`${API}/fabrics/${editFabric.id}`, cleanedData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Updated successfully');
      } else {
        await axios.post(`${API}/fabrics`, cleanedData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Created successfully');
      }
      setOpen(false);
      setEditFabric(null);
      setFormData({ ln_code: 'LN-', vendor_name: '', vendor_code: '', composition: '', width: '', gsm: '', fabric_type: '', colors: [] });
      setCustomFabricType('');
      fetchFabrics();
    } catch (error) {
      // Handle Pydantic validation errors and other error types
      let errorMsg = 'Operation failed';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Check if detail is an array of validation errors
        if (Array.isArray(detail)) {
          errorMsg = detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (detail.msg) {
          errorMsg = detail.msg;
        }
      }
      
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (fabric) => {
    setFabricToDelete(fabric);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!fabricToDelete) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/fabrics/${fabricToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted successfully');
      setDeleteDialogOpen(false);
      setFabricToDelete(null);
      fetchFabrics();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setFabricToDelete(null);
  };

  const handleRemoveColor = (index) => {
    setColorToDelete(index);
    setDeleteColorDialogOpen(true);
  };

  const confirmColorDelete = () => {
    if (colorToDelete === null) return;
    const updatedColors = formData.colors.filter((_, i) => i !== colorToDelete);
    setFormData({ ...formData, colors: updatedColors });
    setDeleteColorDialogOpen(false);
    setColorToDelete(null);
    toast.success('Color removed');
  };

  const cancelColorDelete = () => {
    setDeleteColorDialogOpen(false);
    setColorToDelete(null);
  };

  const handlePrint = () => {
    if (!printBarcode) return;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Barcode - ${printBarcode.code}</title>
          <style>
            @page { 
              size: ${printSize.width}mm ${printSize.height}mm;
              margin: 0;
            }
            @media print {
              body { margin: 0; padding: 0; }
            }
            body { 
              margin: 0; 
              padding: 0; 
              display: flex; 
              justify-content: center; 
              align-items: center; 
              width: ${printSize.width}mm;
              height: ${printSize.height}mm;
            }
            .barcode-container { 
              text-align: center;
              page-break-inside: avoid;
              width: 100%;
              height: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            img { 
              max-width: 100%;
              max-height: 100%;
              display: block;
            }
          </style>
        </head>
        <body>
          <div class="barcode-container">
            <img src="${printBarcode.image}" alt="Barcode" onload="window.print(); window.onafterprint = function() { window.close(); }" />
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Fabric (LN-CODE)</h1>
              <p className="text-sm text-gray-600">Manage fabric specifications</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6 space-y-4">
        {/* Main Search Bar */}
        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search or scan barcode"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleBarcodeSearch}
              className="pl-10"
              autoComplete="off"
            />
          </div>
          {canCreate('master_data') && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={async () => { 
                  setEditFabric(null); 
                  setCustomFabricType('');
                  // Fetch next available code
                  try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${API}/master-data/next-code/fabric`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    if (response.ok) {
                      const data = await response.json();
                      setFormData({ ln_code: data.next_code, vendor_code: '', vendor_name: '', composition: '', width: '', gsm: '', fabric_type: '', colors: [] });
                    } else {
                      setFormData({ ln_code: 'LN-00001', vendor_code: '', vendor_name: '', composition: '', width: '', gsm: '', fabric_type: '', colors: [] });
                    }
                  } catch (error) {
                    console.error('Error fetching next code:', error);
                    setFormData({ ln_code: 'LN-00001', vendor_code: '', vendor_name: '', composition: '', width: '', gsm: '', fabric_type: '', colors: [] });
                  }
                }}>
                  <Plus className="w-4 h-4 mr-2" /> Add Fabric
                </Button>
              </DialogTrigger>
            </Dialog>
          )}
        </div>

        {/* Filters Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">GSM Range</Label>
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder="Min GSM"
                value={gsmMin}
                onChange={(e) => setGsmMin(e.target.value)}
                className="text-sm"
              />
              <Input
                type="number"
                placeholder="Max GSM"
                value={gsmMax}
                onChange={(e) => setGsmMax(e.target.value)}
                className="text-sm"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium">Fabric Type</Label>
            <SearchableSelect
              options={[
                { value: "", label: "All Types" },
                ...FABRIC_TYPES.map(type => ({ value: type, label: type }))
              ]}
              value={fabricTypeFilter?.startsWith('OTHER:') ? 'OTHER' : fabricTypeFilter}
              onChange={(value) => {
                if (value === 'OTHER') {
                  setFabricTypeFilter('OTHER');
                  setCustomFabricTypeFilter('');
                } else {
                  setFabricTypeFilter(value);
                  setCustomFabricTypeFilter('');
                }
              }}
              placeholder="All Types"
              searchPlaceholder="Search fabric type..."
            />
            {(fabricTypeFilter === 'OTHER' || fabricTypeFilter?.startsWith('OTHER:')) && (
              <Input
                value={customFabricTypeFilter}
                onChange={(e) => {
                  setCustomFabricTypeFilter(e.target.value);
                  setFabricTypeFilter(e.target.value ? `OTHER: ${e.target.value}` : 'OTHER');
                }}
                placeholder="Type custom fabric type"
                className="text-sm mt-2"
              />
            )}
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium">Color Name</Label>
            <Input
              placeholder="Search by color name"
              value={colorNameFilter}
              onChange={(e) => setColorNameFilter(e.target.value)}
              className="text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Supplier</Label>
            <SearchableSelect
              options={[
                { value: '', label: 'All Suppliers' },
                ...Array.from(new Set(fabrics.map(f => f.vendor_name).filter(Boolean)))
                  .sort()
                  .map(supplier => ({ value: supplier, label: supplier }))
              ]}
              value={supplierFilter}
              onChange={setSupplierFilter}
              placeholder="Select supplier..."
              className="text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Vendor Code</Label>
            <Input
              type="text"
              value={vendorCodeFilter}
              onChange={(e) => setVendorCodeFilter(e.target.value)}
              placeholder="Type vendor code..."
              className="text-sm"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium">Actions</Label>
            <Button 
              variant="outline" 
              onClick={() => {
                setSearchQuery('');
                setGsmMin('');
                setGsmMax('');
                setFabricTypeFilter('');
                setColorNameFilter('');
                setSupplierFilter('');
                setVendorCodeFilter('');
              }}
              className="w-full text-sm"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) setErrorMessage(''); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-4xl">
            <DialogHeader><DialogTitle>{editFabric ? 'Edit Fabric' : 'Add New Fabric (LN-CODE)'}</DialogTitle></DialogHeader>
            {errorMessage && (
              <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-bold text-red-800 mb-1">Error</h3>
                    <p className="text-sm text-red-700">{errorMessage}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setErrorMessage('')}
                    className="ml-auto flex-shrink-0 inline-flex text-red-400 hover:text-red-600"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label><span className="text-red-500">*</span> LN Code</Label>
                <Input 
                  value={formData.ln_code} 
                  onChange={(e) => {
                    setFormData({ ...formData, ln_code: e.target.value });
                  }}
                  required 
                  placeholder="LN-00001" 
                />
                <p className="text-xs text-gray-500">Auto-generated, but you can edit it</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label><span className="text-red-500">*</span> Vendor</Label>
                  <SearchableSelect
                    options={[
                      { value: "", label: "Select Vendor" },
                      ...vendors.map(v => ({ value: v.vendor_name, label: v.vendor_name }))
                    ]}
                    value={formData.vendor_name}
                    onChange={(value) => setFormData({ ...formData, vendor_name: value })}
                    placeholder="Select Vendor"
                    searchPlaceholder="Search vendor..."
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Vendor Code</Label>
                  <Input value={formData.vendor_code} onChange={(e) => setFormData({ ...formData, vendor_code: e.target.value })} placeholder="Enter vendor code" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Composition</Label>
                  <Input value={formData.composition} onChange={(e) => setFormData({ ...formData, composition: e.target.value })} placeholder="Enter fabric composition" />
                </div>
                <div className="space-y-2">
                  <Label>Width (inches)</Label>
                  <Input type="number" value={formData.width} onChange={(e) => setFormData({ ...formData, width: e.target.value ? parseFloat(e.target.value) : '' })} placeholder="Enter value" />
                </div>
                <div className="space-y-2">
                  <Label>GSM</Label>
                  <Input type="number" value={formData.gsm} onChange={(e) => setFormData({ ...formData, gsm: e.target.value ? parseFloat(e.target.value) : '' })} placeholder="Enter value" />
                </div>
              </div>
              <div className="space-y-2">
                <Label><span className="text-red-500">*</span> Fabric Type</Label>
                <SearchableSelect
                  options={[
                    { value: "", label: "Select Fabric Type" },
                    ...FABRIC_TYPES.map(type => ({ value: type, label: type }))
                  ]}
                  value={formData.fabric_type?.startsWith('OTHER') ? 'OTHER' : formData.fabric_type}
                  onChange={(value) => {
                    if (value === 'OTHER') {
                      setFormData({ ...formData, fabric_type: 'OTHER' });
                      setCustomFabricType('');
                    } else {
                      setFormData({ ...formData, fabric_type: value });
                      setCustomFabricType('');
                    }
                  }}
                  placeholder="Select Fabric Type"
                  searchPlaceholder="Search fabric type..."
                  required
                />
                {(formData.fabric_type === 'OTHER' || formData.fabric_type?.startsWith('OTHER:')) && (
                  <div className="mt-2">
                    <Input 
                      value={customFabricType} 
                      onChange={(e) => {
                        setCustomFabricType(e.target.value);
                        setFormData({ ...formData, fabric_type: e.target.value ? `OTHER: ${e.target.value}` : 'OTHER' });
                      }} 
                      placeholder="Enter custom fabric type" 
                      required 
                    />
                  </div>
                )}
              </div>
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Add Colors</h3>
                <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs mb-1">Color Code</Label>
                      <Input value={currentColor.color_code} onChange={(e) => setCurrentColor({ ...currentColor, color_code: e.target.value })} placeholder="Color code" />
                    </div>
                    <div>
                      <Label className="text-xs mb-1">Color Name</Label>
                      <Input value={currentColor.color_name} onChange={(e) => setCurrentColor({ ...currentColor, color_name: e.target.value })} placeholder="Color name" />
                    </div>
                    <div>
                      <Label className="text-xs mb-1">Price</Label>
                      <Input type="number" step="0.01" value={currentColor.price} onChange={(e) => setCurrentColor({ ...currentColor, price: parseFloat(e.target.value) })} placeholder="$" />
                    </div>
                    <div>
                      <Label className="text-xs mb-1"><span className="text-red-500">*</span> UOM</Label>
                      <SearchableSelect
                        options={[
                          { value: "meter", label: "Meter" },
                          { value: "yard", label: "Yard" },
                          { value: "kg", label: "KG" }
                        ]}
                        value={currentColor.uom}
                        onChange={(value) => setCurrentColor({ ...currentColor, uom: value })}
                        placeholder="Select UOM"
                        searchPlaceholder="Search UOM..."
                        required
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" onClick={addColor}>
                      {editingColorIndex !== null ? (
                        <>
                          <Pencil className="w-4 h-4 mr-2" /> Update Color
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" /> Add Color
                        </>
                      )}
                    </Button>
                    {editingColorIndex !== null && (
                      <Button type="button" variant="outline" onClick={() => { setEditingColorIndex(null); setCurrentColor({ color_code: '', color_name: '', price: '', uom: 'meter', price_per_meter: '' }); }}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
                {formData.colors.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {formData.colors.map((color, index) => (
                      <div key={index} className="flex items-center justify-between bg-white p-3 rounded border">
                        <div>
                          <span className="font-medium">{color.color_code}</span> - {color.color_name}
                          {color.price && ` ($${color.price} / ${color.uom})`}
                          {!color.price && <span className="text-gray-400 text-sm ml-2">(No price set)</span>}
                        </div>
                        <div className="flex gap-2">
                          <Button type="button" variant="ghost" size="icon" onClick={() => editColor(index)}>
                            <Pencil className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeColor(index)}>
                            <X className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button type="submit" className="w-full">{editFabric ? 'Update' : 'Create'} Fabric</Button>
            </form>
          </DialogContent>
        </Dialog>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {filteredFabrics.map((fabric) => (
          <div key={fabric.id} className="bg-white border-2 border-black rounded-lg shadow p-4 flex flex-col items-center justify-between hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => setViewDetails(fabric)}>
            <h3 className="text-lg font-bold text-center mb-2">{fabric.ln_code}</h3>
            <p className="text-xs text-gray-500 text-center mb-3">{fabric.colors?.length || 0} colors</p>
            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              {canEdit('master_data') && (
                <Button variant="ghost" size="icon" onClick={() => { 
                  setEditFabric(fabric); 
                  setFormData(fabric);
                  if (fabric.fabric_type?.startsWith('OTHER:')) {
                    setCustomFabricType(fabric.fabric_type.replace('OTHER: ', ''));
                  }
                  setOpen(true); 
                }}>
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
              {canDelete('master_data') && (
                <Button variant="ghost" size="icon" onClick={() => handleDelete(fabric)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredFabrics.length === 0 && (
        <div className="text-center py-12"><p className="text-gray-500">No fabrics found.</p></div>
      )}

      <Dialog open={!!viewDetails} onOpenChange={() => { setViewDetails(null); setColorSearchQuery(''); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Fabric Details - {viewDetails?.ln_code}</DialogTitle></DialogHeader>
          {viewDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="font-semibold">LN Code:</span> {viewDetails.ln_code}</div>
                {viewDetails.vendor_name && (
                  <div className="col-span-2 flex items-center gap-2">
                    <span className="font-semibold">Vendor:</span>
                    <span 
                      onClick={() => handleVendorClick(viewDetails.vendor_name)}
                      className="cursor-pointer hover:underline text-blue-600"
                    >
                      {viewDetails.vendor_name}
                    </span>
                    {(() => {
                      const vendor = vendors.find(v => v.vendor_name === viewDetails.vendor_name);
                      return vendor && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          vendor.local_or_imported === 'Local' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {vendor.local_or_imported}-{vendor.country}
                        </span>
                      );
                    })()}
                  </div>
                )}
                {viewDetails.vendor_code && (
                  <div><span className="font-semibold">Vendor Code:</span> {viewDetails.vendor_code}</div>
                )}
                <div><span className="font-semibold">Composition:</span> {viewDetails.composition}</div>
                <div><span className="font-semibold">Width:</span> {viewDetails.width} inches</div>
                <div><span className="font-semibold">GSM:</span> {viewDetails.gsm}</div>
                <div><span className="font-semibold">Fabric Type:</span> {viewDetails.fabric_type}</div>
              </div>
              
              {/* Main Fabric Barcode Section */}
              {viewDetails.barcode_path && (
                <div className="border-t pt-4 pb-4 border-b">
                  <div className="space-y-2 text-center">
                    <Button variant="outline" size="sm" onClick={() => setPrintBarcode({ 
                      image: `${API}/${viewDetails.barcode_path}`, 
                      code: viewDetails.ln_code 
                    })}>
                      <Printer className="w-3 h-3 mr-1" /> Print Main Barcode
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold">Colors ({viewDetails.colors?.filter(color => {
                    if (!colorSearchQuery.trim()) return true;
                    
                    let query = colorSearchQuery.trim();
                    
                    // If it's a full barcode (e.g., "LN-00095-001"), extract just the color part
                    if (query.includes('-') && query.split('-').length >= 3) {
                      query = query.split('-').pop(); // Get the last part (color code)
                    }
                    
                    // Clean the query and color code for comparison
                    query = query.replace(/\D/g, '').replace(/^0+/, '') || query;
                    const colorCode = color.color_code.replace(/\D/g, '').replace(/^0+/, '') || color.color_code;
                    
                    // Match by color code, color name, or barcode
                    return colorCode.includes(query) || 
                           color.color_code.toLowerCase().includes(colorSearchQuery.toLowerCase()) ||
                           color.color_name.toLowerCase().includes(colorSearchQuery.toLowerCase());
                  }).length || 0})</h4>
                  <div className="relative flex-1 max-w-xs ml-4">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input 
                      placeholder="Search by code, name, or barcode" 
                      value={colorSearchQuery}
                      onChange={(e) => setColorSearchQuery(e.target.value)}
                      className="pl-10"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      tabIndex="2"
                      data-barcode-input="true"
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  {viewDetails.colors?.filter(color => {
                    if (!colorSearchQuery.trim()) return true;
                    
                    let query = colorSearchQuery.trim();
                    
                    // If it's a full barcode (e.g., "LN-00095-001"), extract just the color part
                    if (query.includes('-') && query.split('-').length >= 3) {
                      query = query.split('-').pop(); // Get the last part (color code)
                    }
                    
                    // Clean the query and color code for comparison
                    query = query.replace(/\D/g, '').replace(/^0+/, '') || query;
                    const colorCode = color.color_code.replace(/\D/g, '').replace(/^0+/, '') || color.color_code;
                    
                    // Match by color code, color name, or barcode
                    return colorCode.includes(query) || 
                           color.color_code.toLowerCase().includes(colorSearchQuery.toLowerCase()) ||
                           color.color_name.toLowerCase().includes(colorSearchQuery.toLowerCase());
                  }).map((color, idx) => (
                    <div key={idx} className="border rounded p-4 bg-gray-50">
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div><span className="font-medium">Code:</span> {color.barcode?.code || `${viewDetails.ln_code}-${color.color_code}`}</div>
                        <div><span className="font-medium">Name:</span> {color.color_name}</div>
                        <div><span className="font-medium">Price:</span> ${color.price} / {color.uom}</div>
                        <div><span className="font-medium">Price/Meter:</span> ${color.price_per_meter?.toFixed(2)}</div>
                      </div>
                      {color.barcode_path && (
                        <div className="space-y-2">
                          <Button variant="outline" size="sm" className="w-full" onClick={() => setPrintBarcode({ 
                            image: `${API}/${color.barcode_path}`, 
                            code: `${viewDetails.ln_code}-${color.color_code}` 
                          })}>
                            <Printer className="w-3 h-3 mr-1" /> Print Barcode
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!printBarcode} onOpenChange={() => setPrintBarcode(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Print Barcode - {printBarcode?.code}</DialogTitle></DialogHeader>
          {printBarcode && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <img src={printBarcode.image} alt="Barcode" className="max-w-full" />
              </div>
              <div className="space-y-3">
                <Label>Print Size (mm)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Width</Label>
                    <Input type="number" value={printSize.width} onChange={(e) => setPrintSize({ ...printSize, width: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-sm">Height</Label>
                    <Input type="number" value={printSize.height} onChange={(e) => setPrintSize({ ...printSize, height: e.target.value })} />
                  </div>
                </div>
              </div>
              <Button onClick={handlePrint} className="w-full">
                <Printer className="w-4 h-4 mr-2" /> Print Barcode
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Vendor Details Popup Modal */}
      <Dialog open={!!vendorDetailsModal} onOpenChange={() => setVendorDetailsModal(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Vendor Details - {vendorDetailsModal?.vendor_name}</DialogTitle>
          </DialogHeader>
          {vendorDetailsModal && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="font-semibold">Name:</span> {vendorDetailsModal.vendor_name}</div>
                <div>
                  <span className="font-semibold">Type: </span>
                  <span className={`px-2 py-1 rounded text-sm font-medium ${
                    vendorDetailsModal.local_or_imported === 'Local' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {vendorDetailsModal.local_or_imported}
                  </span>
                </div>
                <div><span className="font-semibold">Vendor Type:</span> {vendorDetailsModal.vendor_type || 'N/A'}</div>
                <div><span className="font-semibold">Phone:</span> {vendorDetailsModal.phone_number || 'N/A'}</div>
                <div><span className="font-semibold">Alt Number:</span> {vendorDetailsModal.alt_number || 'N/A'}</div>
                <div><span className="font-semibold">City:</span> {vendorDetailsModal.city}</div>
                <div><span className="font-semibold">Country:</span> {vendorDetailsModal.country}</div>
                <div className="col-span-2"><span className="font-semibold">Address:</span> {vendorDetailsModal.address || 'N/A'}</div>
              </div>
              {vendorDetailsModal.wechat_qr_code && (
                <div className="mt-4 pt-4 border-t">
                  <p className="font-semibold mb-2">WeChat QR Code:</p>
                  <img 
                    src={vendorDetailsModal.wechat_qr_code} 
                    alt="WeChat QR Code" 
                    className="w-48 h-48 object-contain border rounded p-2"
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Authorization Dialog */}
      <AuthorizationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onAuthorized={confirmDelete}
        title="Delete Fabric"
        message={fabricToDelete ? `Enter authorization code to delete fabric ${fabricToDelete.ln_code}` : 'Enter authorization code to proceed'}
      />

      {/* Delete Color Authorization Dialog */}
      <AuthorizationDialog
        open={deleteColorDialogOpen}
        onOpenChange={setDeleteColorDialogOpen}
        onAuthorized={confirmColorDelete}
        title="Remove Color"
        message={colorToDelete !== null && formData.colors[colorToDelete] ? `Enter authorization code to remove color ${formData.colors[colorToDelete].color_name}` : 'Enter authorization code to proceed'}
      />
      </main>
    </div>
  );
}